import { ReviewGetType, ReviewInterface, ReviewPlatform } from "@swivl-mono/types";
import { Button, Checkbox, Spinner } from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";

import { Pagination } from "../../Components/Pagination/Pagination";
import LocationsModel from "../../Models/Locations/Locations.Model";
import { NavBar } from "../../Models/NavBar/NavBar.Model";
import ReviewsModel from "../../Models/Reviews/Reviews.Model";
import { ReviewsManagerViewFilters, getReviewsViewData, isReviewDeletedOnGoogle } from "./Helpers/ReviewsViewHelper";
import { ReviewsManagerFiltersView } from "./Review/Components/Review.View.Filters";
import { ReviewsViewMultiSelect } from "./Review/Components/Reviews.View.MultiSelect";
import "./Reviews.View.scss";

export default function ReviewsView() {
  const { selectedReviews, paginatedReviews, aggregatedData } = ReviewsModel.useState();
  const { data: displayedReviews, pagination } = paginatedReviews;

  const [filters, setFilters] = useState<ReviewsManagerViewFilters>({
    responded: false,
    notResponded: true,
    deletedOnGoogle: false,
    starRating1: false,
    starRating2: false,
    starRating3: false,
    starRating4: false,
    starRating5: false,
    google: false,
    yelp: false,
    spot: false,
    stores: {}
  });

  const [reviewStatus, setReviewGetType] = useState<ReviewGetType>(ReviewGetType.UNARCHIVED);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [showMultiSelect, setShowMultiSelect] = useState<boolean>(false);
  const [showAllStores, setShowAllStores] = useState<boolean>(true);
  const mainColRef = useRef<HTMLDivElement>(null);

  const getFilterParams = useMemo(() => {
    const params: {
      hasReply?: boolean;
      rating?: number;
      platform?: ReviewPlatform;
      locationId?: number[];
      deletedOnGoogle?: boolean;
    } = {};

    if (filters.notResponded && !filters.responded) {
      params.hasReply = false;
    } else if (filters.responded && !filters.notResponded) {
      params.hasReply = true;
    }

    if (filters.deletedOnGoogle) params.deletedOnGoogle = true;

    if (filters.starRating1) params.rating = 1;
    else if (filters.starRating2) params.rating = 2;
    else if (filters.starRating3) params.rating = 3;
    else if (filters.starRating4) params.rating = 4;
    else if (filters.starRating5) params.rating = 5;

    if (filters.google) params.platform = ReviewPlatform.GOOGLE;
    else if (filters.yelp) params.platform = ReviewPlatform.YELP;
    else if (filters.spot) params.platform = ReviewPlatform.SPOT;

    const selectedLocationIds = Object.keys(filters.stores)
      .filter((id) => filters.stores[parseInt(id)])
      .map((id) => parseInt(id));
    if (selectedLocationIds.length > 0) {
      params.locationId = selectedLocationIds;
    }

    return params;
  }, [filters]);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);

    NavBar.set(<NavBar.Component.Title title={"Reviews"} />, null, null, "Reviews");
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    ReviewsModel.load(
      reviewStatus,
      page,
      limit,
      getFilterParams.hasReply,
      getFilterParams.rating,
      getFilterParams.platform,
      getFilterParams.locationId,
      getFilterParams.deletedOnGoogle
    );
    ReviewsModel.loadAggregatedData(reviewStatus);
    setHasLoaded(true);
  }, [reviewStatus, page, limit, getFilterParams]);

  useEffect(() => {
    if (displayedReviews && displayedReviews.length > 0) {
      if (mainColRef.current) {
        mainColRef.current.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [page, displayedReviews]);

  const reviewViews = useMemo(() => {
    if (!displayedReviews) {
      return [];
    }
    return getReviewsViewData(displayedReviews, selectedReviews, showMultiSelect);
  }, [displayedReviews, selectedReviews, showMultiSelect]);

  const counts = useMemo(() => {
    // Google-deleted reviews aren't selectable - multi-select is reply-only, and there's nothing to reply to.
    const selectableRows = displayedReviews
      ? displayedReviews.reduce(
          (acc, review) => {
            if (!isReviewDeletedOnGoogle(review)) acc[review.id] = review;
            return acc;
          },
          {} as { [id: number]: ReviewInterface }
        )
      : {};

    const selected = displayedReviews ? displayedReviews.filter((review) => selectedReviews[review.id] !== undefined).length : 0;

    return {
      responded: aggregatedData.status.responded,
      notResponded: aggregatedData.status.notResponded,
      deletedOnGoogle: aggregatedData.status.deletedOnGoogle,
      starRating1: aggregatedData.starRating[1],
      starRating2: aggregatedData.starRating[2],
      starRating3: aggregatedData.starRating[3],
      starRating4: aggregatedData.starRating[4],
      starRating5: aggregatedData.starRating[5],
      google: aggregatedData.provider.google || 0,
      yelp: aggregatedData.provider.yelp || 0,
      spot: aggregatedData.provider.spot || 0,
      stores: aggregatedData.locations.reduce((acc, loc) => ({ ...acc, [loc.id]: loc.count }), {}),
      total: pagination.total,
      filtered: pagination.total,
      selected,
      selectableRows
    };
  }, [displayedReviews, selectedReviews, aggregatedData, pagination.total]);

  const selectAll = () => {
    ReviewsModel.setSelectedReviews(counts.selectableRows);
  };
  const selectNone = () => {
    ReviewsModel.setSelectedReviews({});
    setShowMultiSelect(false);
  };

  const allSelected = counts.selected > 0 && counts.selected === Object.keys(counts.selectableRows).length;

  return (
    <div className="ReviewsView">
      <div className="mainCol" ref={mainColRef}>
        <div className="topBar">
          <div className="topBarLeft">
            <Checkbox
              disabled={!displayedReviews || !displayedReviews.length}
              name="selectAll"
              checked={allSelected}
              label="Select All"
              onCheckedChange={() => {
                if (allSelected) {
                  selectNone();
                } else {
                  selectAll();
                }
              }}
            />
          </div>
          {counts.selected > 1 ? (
            <>
              <Button
                onClick={() => {
                  setShowMultiSelect(true);
                }}
              >
                {" "}
                Reply to selected
              </Button>
            </>
          ) : null}
        </div>

        {paginatedReviews.data ? (
          <>
            {reviewViews}
            <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />
          </>
        ) : (
          <div className="loadingReviews">
            <Spinner />
          </div>
        )}
      </div>
      <div className="sideCol">
        {showMultiSelect ? null : (
          <div className="sideColHeader">
            <div
              className={reviewStatus === ReviewGetType.UNARCHIVED ? "filterItem selected" : "filterItem"}
              onClick={() => {
                setReviewGetType(ReviewGetType.UNARCHIVED);
                // The Deleted on Google filter only exists on the Archived tab - a value left
                // behind from there would silently narrow the Open list.
                if (filters.deletedOnGoogle) setFilters({ ...filters, deletedOnGoogle: false });
              }}
            >
              Open
            </div>
            <div
              className={reviewStatus === ReviewGetType.ARCHIVED ? "filterItem selected" : "filterItem"}
              onClick={() => setReviewGetType(ReviewGetType.ARCHIVED)}
            >
              Archived
            </div>
          </div>
        )}
        {showMultiSelect ? (
          <ReviewsViewMultiSelect reviews={selectedReviews} clearSelectedReviews={selectNone} />
        ) : (
          <ReviewsManagerFiltersView
            filters={filters}
            counts={counts}
            setFilters={setFilters}
            storesWithReviews={aggregatedData.locations.map((l) => l.id)}
            showAllStores={showAllStores}
            showDeletedOnGoogleFilter={reviewStatus === ReviewGetType.ARCHIVED}
            setShowAllStores={setShowAllStores}
          />
        )}
      </div>
    </div>
  );
}
